import { useState } from 'react'
import { useSessao } from '../../../auth/contexto'
import { Botao } from '../../../components/Botao'
import { IconeNovo } from '../../../components/Icones'
import { TEXTOS } from '../../../lib/glossario'
import type { UsuarioLogado } from '../../../types/gmud'
import { DialogoDeImportacao } from './DialogoDeImportacao'

/**
 * Os papéis que carregam planilha, escritos exatamente como o servidor os exige.
 *
 * O `@PreAuthorize("hasAnyRole('FISCAL', 'ADMIN')")` do `ImportacaoDeChamadosController` é a regra;
 * isto aqui é conveniência de tela. Não é uma segunda lista de permissões que possa divergir da
 * primeira: quem forçar o caminho recebe 403 do servidor, com a mensagem dele, e é lá que a regra
 * mora. O que esta lista evita é oferecer a alguém um botão que só vai recusá-lo depois de a pessoa
 * ter escolhido o arquivo.
 */
const PAPEIS_QUE_CARREGAM = ['FISCAL', 'ADMIN']

/**
 * O botão que abre a carga da planilha, e o diálogo que ele abre.
 *
 * Vive num componente só porque as duas telas que o oferecem — a lista de chamados e o fechamento
 * do mês — precisam do mesmo par, e um botão sem o diálogo ao lado é meio caminho que cada tela
 * completaria à sua maneira.
 *
 * @param props a aparência do botão, quando a tela precisa de um secundário
 * @returns o botão de carga, ou nada para quem não carrega planilha
 */
export function BotaoDeCarga({ aparencia = 'primario' }: { aparencia?: 'primario' | 'secundario' }) {
  const [aberto, setAberto] = useState(false)
  const { usuario } = useSessao()

  if (!podeCarregar(usuario)) {
    return null
  }

  return (
    <>
      <Botao aparencia={aparencia} onClick={() => setAberto(true)}>
        <IconeNovo className="h-4 w-4" />
        {TEXTOS.importacao.botao}
      </Botao>
      {aberto && <DialogoDeImportacao aoFechar={() => setAberto(false)} />}
    </>
  )
}

/** @returns verdadeiro quando o usuário logado tem papel de fiscalização */
function podeCarregar(usuario: UsuarioLogado | null): boolean {
  return usuario?.papeis.some((papel) => PAPEIS_QUE_CARREGAM.includes(papel)) ?? false
}
